import { CheckIcon, ChevronDownIcon } from '@chakra-ui/icons'
import {
  Box,
  Center,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react'
import { colors, FieldMap, FieldMapValue, Form, InfoStage, isInfoStage } from '@hb/shared'
import { get as nestedGet, set as nestedSet } from 'nested-property'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useResizeObserver } from '../../../hooks'
import { DataView } from '../../DataView/DataView'
import { useFormWizardData, useStageState } from './hooks'
import { FormWizardProps } from './types'

interface StageTabPanelProps {
  stageId: string
  stage: FieldMap | InfoStage
  form: Form
  formData: FieldMapValue | undefined
  formCorrections: (FieldMapValue & { correctionsShared?: any | null }) | null | undefined
  onSubmit: ((submitted: FieldMapValue) => Promise<any>) | null
  getStoragePath?: ((stageId: string) => string | null) | undefined
  adminView?: boolean
}

const StageTabPanel: React.FC<StageTabPanelProps> = ({
  stageId,
  stage,
  form,
  formData,
  formCorrections,
  onSubmit,
  getStoragePath,
  adminView,
}) => {
  const stageState = useStageState(form, stage, formData, formCorrections, stageId)

  const storagePath = useMemo(
    () => (getStoragePath ? getStoragePath(stageId) : null),
    [getStoragePath, stageId],
  )

  const updateField = useCallback(
    async (path: string, value: any) => {
      if (!onSubmit) return { error: 'No onSubmit function' }

      const stagePath = `${form?.path ? `${form.path}.` : ''}${stageId}`
      const fullPath = path ? `${stagePath}.${path}` : stagePath

      // Get current stage data
      const currentStageData = nestedGet(formData ?? {}, stagePath) as FieldMapValue | undefined

      // Update the value
      const updated = { ...(currentStageData ?? {}) }
      if (path) {
        nestedSet(updated, path, value)
      } else {
        Object.assign(updated, value)
      }

      // Create the update object
      const updateObj: FieldMapValue = {}
      nestedSet(updateObj, fullPath, updated)

      try {
        const result = await onSubmit(updateObj)
        if (result?.error) {
          return { error: result.error }
        }
        return { success: 'Updated successfully' }
      } catch (e: any) {
        return { error: e?.message || 'Error updating field' }
      }
    },
    [onSubmit, form, formData, stageId],
  )

  // Skip info stages - they don't need DataView
  if (isInfoStage(stage)) {
    return <TabPanel>{/* Info stages can be handled differently if needed */}</TabPanel>
  }

  // TypeScript: stage is narrowed to FieldMap after the isInfoStage check
  const fieldMapStage = stage

  return (
    <TabPanel bg="white" h="100%" overflowY="auto">
      <DataView
        field={fieldMapStage}
        data={stageState.value}
        corrections={stageState.corrections}
        updateField={updateField}
        baseStoragePath={storagePath}
        adminView={adminView}
        alwaysExpanded={true}
        noHeader={false}
        fieldPathSegments={form?.path ? [form.path, stageId] : [stageId]}
      />
    </TabPanel>
  )
}

interface MobileStagePanelProps {
  stageId: string
  stage: FieldMap | InfoStage
  form: Form
  formData: FieldMapValue | undefined
  formCorrections: (FieldMapValue & { correctionsShared?: any | null }) | null | undefined
  onSubmit: ((submitted: FieldMapValue) => Promise<any>) | null
  getStoragePath?: ((stageId: string) => string | null) | undefined
  adminView?: boolean
}

const MobileStagePanel: React.FC<MobileStagePanelProps> = ({
  stageId,
  stage,
  form,
  formData,
  formCorrections,
  onSubmit,
  getStoragePath,
  adminView,
}) => {
  const stageState = useStageState(form, stage, formData, formCorrections, stageId)

  const storagePath = useMemo(
    () => (getStoragePath ? getStoragePath(stageId) : null),
    [getStoragePath, stageId],
  )

  const updateField = useCallback(
    async (path: string, value: any) => {
      if (!onSubmit) return { error: 'No onSubmit function' }

      const stagePath = `${form?.path ? `${form.path}.` : ''}${stageId}`
      const fullPath = path ? `${stagePath}.${path}` : stagePath

      const updated = {}
      if (path) {
        nestedSet(updated, fullPath, value)
      } else {
        Object.assign(updated, value)
      }

      try {
        const result = await onSubmit(updated)
        if (result?.error) {
          return { error: result.error }
        }
        return { success: 'Updated successfully' }
      } catch (e: any) {
        return { error: e?.message || 'Error updating field' }
      }
    },
    [onSubmit, form, stageId],
  )

  if (isInfoStage(stage)) {
    return null
  }

  return (
    <Box bg="white" h="100%" overflowY="auto" borderWidth={1} borderColor="#cdcdcd">
      <DataView
        field={stage}
        initExpanded
        childrenExpanded
        data={stageState.value}
        corrections={stageState.corrections}
        updateField={updateField}
        baseStoragePath={storagePath}
        adminView={adminView}
        alwaysExpanded={true}
        // noHeader={false}
        noHeader
        fieldPathSegments={form?.path ? [form.path, stageId] : [stageId]}
      />
    </Box>
  )
}

export const FormWizardTabsView = ({
  patientId,
  assessmentId,
  form,
  data,
  corrections,
  onSubmit,
  stageId: stageIdProp,
  selectStage,
  adminView,
  getStoragePath,
  renderStage,
  readOnly,
  ReadOnlyFooter,
  mobileViewBreakpoint = 1000,
  headerRightItem,
}: FormWizardProps) => {
  const formWizardData = useFormWizardData(
    patientId,
    assessmentId,
    form,
    data,
    corrections,
    onSubmit,
    stageIdProp,
    selectStage,
    renderStage,
    readOnly,
    ReadOnlyFooter,
    getStoragePath,
    adminView,
  )

  const { data: formData, corrections: formCorrections } = formWizardData

  // Get all stage keys
  const stageKeys = useMemo(() => Object.keys(form?.stages ?? {}), [form])

  // Use the first stage as default if no stageId is provided
  const defaultTabIndex = useMemo(() => {
    if (stageIdProp && stageKeys.includes(stageIdProp)) {
      return stageKeys.indexOf(stageIdProp)
    }
    return 0
  }, [stageIdProp, stageKeys])

  const [selectedTabIndex, setSelectedTabIndex] = useState(defaultTabIndex)

  // Sync tab index with stageId prop changes
  const currentTabIndex = useMemo(() => {
    if (stageIdProp && stageKeys.includes(stageIdProp)) {
      return stageKeys.indexOf(stageIdProp)
    }
    return selectedTabIndex
  }, [stageIdProp, stageKeys, selectedTabIndex])

  const handleTabChange = useCallback(
    (index: number) => {
      setSelectedTabIndex(index)
      const newStageId = stageKeys[index] ?? null
      if (selectStage) {
        selectStage(newStageId)
      } else {
        formWizardData.open(newStageId ?? '')
      }
    },
    [stageKeys, selectStage, formWizardData],
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const { width } = useResizeObserver(containerRef, 'content')

  const isMobile = useMemo(
    () => width > 0 && width < mobileViewBreakpoint,
    [width, mobileViewBreakpoint],
  )

  const activeStage = useMemo(() => {
    const stageId = stageKeys[currentTabIndex]
    return stageId ? form?.stages?.[stageId] : null
  }, [currentTabIndex, stageKeys, form])

  const currentStageId = stageKeys[currentTabIndex]

  // Check if a stage has data (indicating it's been started/completed)
  const isStageComplete = useCallback(
    (stageId: string) => {
      const stagePath = `${form?.path ? `${form.path}.` : ''}${stageId}`
      const stageData = nestedGet(formData ?? {}, stagePath) as FieldMapValue | undefined
      return stageData && Object.keys(stageData).length > 0
    },
    [form, formData],
  )

  return (
    <Box ref={containerRef} w="100%" h="100%">
      {isMobile ? (
        <Flex flexDir="column" h="100%" w="100%">
          <Flex py={2} gap={2} align="center">
            <Box flex={1}>
              <Menu matchWidth>
                <MenuButton boxShadow="1px 1px 5px #00000055" borderRadius={6}>
                  <Flex
                    py={1.5}
                    px={3}
                    w="100%"
                    bg="white"
                    align="center"
                    borderRadius={6}
                    gap={1.5}>
                    {isStageComplete(currentStageId) ? (
                      <Center borderRadius="full" w={5} h={5} bg="green.400">
                        <CheckIcon w={3} h={3} color="white" />
                      </Center>
                    ) : null}
                    <Text textAlign="left" flex={1} minW={0} fontWeight={500} fontFamily="Hero-New">
                      {activeStage?.name ?? 'Select Stage'}
                    </Text>
                    <ChevronDownIcon ml={5} w={5} h={5} />
                  </Flex>
                </MenuButton>
                <MenuList gap={0} p={0} boxShadow="1px 1px 5px #00000055" border="none">
                  {stageKeys.map((stageId, i) => {
                    const stage = form?.stages?.[stageId]
                    if (!stage) return null
                    const complete = isStageComplete(stageId)
                    return (
                      <MenuItem
                        px={0}
                        bg="transparent"
                        key={stageId}
                        onClick={() => handleTabChange(i)}
                        borderTop={i ? '1px solid #cdcdcd' : 'none'}
                        _focus={{ bg: 'transparent' }}
                        _hover={{ bg: 'blackAlpha.100' }}>
                        <Flex py={1} px={3} w="100%" align="center" borderRadius={6} gap={2}>
                          <Text
                            lineHeight={1.2}
                            textAlign="left"
                            flex={1}
                            minW={0}
                            fontFamily="Hero-New">
                            {stage.name}
                          </Text>
                          <CheckIcon w={4} h={4} color={complete ? colors.green.hex : '#999999'} />
                        </Flex>
                      </MenuItem>
                    )
                  })}
                </MenuList>
              </Menu>
            </Box>
            {headerRightItem ?? null}
          </Flex>
          <Box bg="white" flex={1} minH={0}>
            {currentStageId && activeStage ? (
              <MobileStagePanel
                stageId={currentStageId}
                stage={activeStage}
                form={form}
                formData={formData}
                formCorrections={formCorrections}
                onSubmit={onSubmit}
                getStoragePath={getStoragePath}
                adminView={adminView}
              />
            ) : null}
          </Box>
        </Flex>
      ) : (
        <Tabs
          h="100%"
          w="100%"
          index={currentTabIndex}
          onChange={handleTabChange}
          variant="enclosed"
          colorScheme="green">
          <Flex align="flex-end">
            <TabList>
              {stageKeys.map(stageId => {
                const stage = form?.stages?.[stageId]
                if (!stage) return null
                return (
                  <Tab
                    _selected={{
                      bg: 'white',
                      borderTopRadius: 6,
                      fontWeight: 600,
                      borderColor: '#cdcdcd',
                      borderTopWidth: 1,
                      borderLeftWidth: 1,
                      borderRightWidth: 1,
                      borderBottomWidth: 0,
                    }}
                    lineHeight={1.2}
                    borderBottomWidth={0}
                    py={2}
                    key={stageId}>
                    {stage.name}
                  </Tab>
                )
              })}
            </TabList>
            {headerRightItem ? (
              <Flex align="center" pb={1} pl={2}>
                {headerRightItem}
              </Flex>
            ) : null}
          </Flex>
          <TabPanels
            borderRightWidth={1}
            borderLeftWidth={1}
            borderBottomWidth={1}
            borderTopWidth={1}
            bg="white"
            borderColor="#cdcdcd">
            {stageKeys.map(stageId => {
              const stage = form?.stages?.[stageId]
              if (!stage) return null

              return (
                <StageTabPanel
                  key={stageId}
                  stageId={stageId}
                  stage={stage}
                  form={form}
                  formData={formData}
                  formCorrections={formCorrections}
                  onSubmit={onSubmit}
                  getStoragePath={getStoragePath}
                  adminView={adminView}
                />
              )
            })}
          </TabPanels>
        </Tabs>
      )}
    </Box>
  )
}
